import {
    FC,
    Fragment,
    PropsWithChildren,
    ReactElement,
    cloneElement,
    useCallback,
    useEffect,
    useId,
    useRef,
    useState,
} from 'react';
import classnames from 'classnames';

import { getOffsetElement } from '@hh.ru/magritte-common-metrics';
import { useWhenFontLoaded } from '@hh.ru/magritte-common-use-when-font-loaded';
import { DEFAULT_SIZE, BadgeProps, BadgeSize } from '@hh.ru/magritte-ui-badge/types';

import styles from './badge.less';

const BADGE_OUTLINE_PX = 2;
const FOCUS_OUTLINE_PX = 4;

interface ComponentWithBadgeProps {
    /** Список разрешенных размеров Badge, по умолчанию включает все размеры */
    allowedSizes?: BadgeSize[] | null;
    /** Компонент Badge */
    badge?: ReactElement;
    /** Смещение бейджа относительно правого верхнего угла компонента */
    offset?: number;
    /** Расширять ли внешний контейнер ComponentWithBadge на 100% */
    stretched?: boolean;
}

export const ComponentWithBadge: FC<PropsWithChildren<ComponentWithBadgeProps>> = ({
    allowedSizes = null,
    badge,
    children,
    offset = 0,
    stretched = false,
}) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const componentRef = useRef<HTMLDivElement | null>(null);
    const badgeRef = useRef<HTMLDivElement | null>(null);
    const clipPathRef = useRef<SVGClipPathElement | null>(null);
    const clipPathId = useId();
    const [isDisplayed, setDisplayed] = useState(false);
    const [isFontLoaded, setFontLoaded] = useState(false);

    const badgeProps = badge?.props as BadgeProps | undefined;
    const badgeText = badgeProps?.children;
    const badgeSize = badgeProps?.size ?? DEFAULT_SIZE;

    const recalcClipPath = useCallback(() => {
        if (!wrapperRef.current || !componentRef.current || !badgeRef.current || !clipPathRef.current) {
            return;
        }

        wrapperRef.current.style.setProperty(`--magritte-badge-offset`, `${offset}px`);
        const { offsetLeft: badgeX, offsetTop: badgeY } = getOffsetElement(badgeRef.current, wrapperRef.current);
        const { width: badgeWidth, height: badgeHeight } = badgeRef.current.getBoundingClientRect();
        const borderRadius = badgeHeight / 2;
        const outlineRadius = borderRadius + BADGE_OUTLINE_PX;

        // координаты (0, 0) в svg соответствует левому верхнему углу контента, (1, 1) — правому нижнему углу
        // поэтому нужно посчитать размер одного пикселя в системе координат svg
        const { width: wrapperWidth, height: wrapperHeight } = wrapperRef.current.getBoundingClientRect();
        const xPixelSize = 1 / wrapperWidth;
        const yPixelSize = 1 / wrapperHeight;
        const xSize = (size: number) => size * xPixelSize;
        const ySize = (size: number) => size * yPixelSize;

        // прямоугольник по границам компонента с учетом аутлайна фокуса
        const componentOutline = [
            { x: -xSize(FOCUS_OUTLINE_PX), y: -ySize(FOCUS_OUTLINE_PX) },
            { x: 1 + xSize(FOCUS_OUTLINE_PX), y: -ySize(FOCUS_OUTLINE_PX) },
            { x: 1 + xSize(FOCUS_OUTLINE_PX), y: 1 + ySize(FOCUS_OUTLINE_PX) },
            { x: -xSize(FOCUS_OUTLINE_PX), y: 1 + ySize(FOCUS_OUTLINE_PX) },
        ];

        // прямоугольник с двумя полуокружностями по бокам по границам бейджа с учетом прозрачного аутлайна
        const badgeOutline = [
            { x: xSize(badgeX + borderRadius), y: ySize(badgeY - BADGE_OUTLINE_PX) },
            { x: xSize(badgeX + borderRadius), y: ySize(badgeY + badgeHeight + BADGE_OUTLINE_PX) },
            { x: xSize(badgeX + badgeWidth - borderRadius), y: ySize(badgeY + badgeHeight + BADGE_OUTLINE_PX) },
            { x: xSize(badgeX + badgeWidth - borderRadius), y: ySize(badgeY - BADGE_OUTLINE_PX) },
        ];

        const clipPath = [
            // // M (move) — перейти на точку, ничего не рисовать
            `M ${componentOutline[0].x}, ${componentOutline[0].y}`,
            // L (line) — нарисовать прямую линию
            `L ${componentOutline[1].x}, ${componentOutline[1].y}`,
            `L ${componentOutline[2].x}, ${componentOutline[2].y}`,
            `L ${componentOutline[3].x}, ${componentOutline[3].y}`,
            `L ${componentOutline[0].x}, ${componentOutline[0].y}`,
            `M ${badgeOutline[0].x}, ${badgeOutline[0].y}`,
            // A (arc) — нарисовать часть эллипса
            `A ${xSize(outlineRadius)}, ${ySize(outlineRadius)}, 0, 0, 0, ${badgeOutline[1].x}, ${badgeOutline[1].y}`,
            `L ${badgeOutline[2].x}, ${badgeOutline[2].y}`,
            `A ${xSize(outlineRadius)}, ${ySize(outlineRadius)}, 0, 0, 0, ${badgeOutline[3].x}, ${badgeOutline[3].y}`,
            `L ${badgeOutline[0].x}, ${badgeOutline[0].y}`,
        ].join(` `);
        clipPathRef.current.querySelector(`path`)?.setAttribute(`d`, clipPath);
        clipPathRef.current.setAttribute(`id`, `clip-path-${clipPathId}`);
        componentRef.current.style.setProperty(`clip-path`, `url(#clip-path-${clipPathId})`);
    }, [clipPathId, offset]);

    // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    useEffect(() => {
        if (wrapperRef.current !== null) {
            setDisplayed(wrapperRef.current.offsetParent !== null);
        }
    });

    useWhenFontLoaded(() => {
        setFontLoaded(true);
    });

    useEffect(() => {
        if (isDisplayed && isFontLoaded) {
            recalcClipPath();
        }
    }, [badgeText, badgeSize, isDisplayed, isFontLoaded, recalcClipPath]);

    if (!badge || (allowedSizes !== null && !allowedSizes.includes(badgeSize))) {
        return <Fragment>{children}</Fragment>;
    }

    return (
        <div
            className={classnames(styles.componentWithBadge, { [styles.componentWithBadgeStretched]: stretched })}
            ref={wrapperRef}
        >
            <div ref={componentRef}>{children}</div>
            <div className={classnames(styles.badgeWrapper, { [styles.invisibleWrapper]: !isFontLoaded })}>
                {cloneElement(badge, { ref: badgeRef })}
            </div>
            <svg className={styles.clipPath}>
                <defs>
                    <clipPath clipPathUnits="objectBoundingBox" ref={clipPathRef}>
                        <path />
                    </clipPath>
                </defs>
            </svg>
        </div>
    );
};
